// @flow
import React, { Component } from 'react';
import { connect } from 'react-redux';
import ReactSVG from 'react-svg';

import ComboFilter from '../ComboFilter';
import DateFilter from '../DateFilter';
import { Wrapper, svgStyle } from './style';

import { optionsPlatforms } from '../../pages/AdvancedSearch';
import langues from '../../translations/lang.json';
import Close from '../../assets/images/close.svg';
import { setCriteria, makeSearch, setYearsMustUse } from '../../actions/search';
import { initialState } from '../../reducers/search';
import Button from '../Button';

import type { Select } from '../../types/Select';
import type { Facet } from '../../types/Facet';
import type { SetCriteria, Years } from '../../types/Criteria';
import { FormattedMessage } from 'react-intl';
import { formatMessage } from '../../CurrentLocale';

type Props = {
  counts: Facet,
  search: {
    platforms: {},
    years: Years,
    pagination: {}
  },
  setCriteria: SetCriteria => void,
  setYearsMustUse: boolean => void,
  makeSearch: () => void,
  translations: {}
};

type SelectFacet = {
  ...Select,
  id: string,
  facet: string
};

type State = {
  tags: Array<SelectFacet>
};

const getPlatformLabel = (key: string): string => {
  const { label } = optionsPlatforms.find(({ value }) => value === key) || {
    label: key
  };

  return label;
};

const cleanLabel = (label = '') => {
  return label
    .replace(/./, label.toUpperCase()[0])
    .replace(/&amp;/g, '&')
    .replace(/<\/?sup>/gm, '');
};

class FacetFilter extends Component<Props, State> {
  state = {
    tags: []
  };

  static getDerivedStateFromProps(props, state) {
    const {
      search: {
        platforms,
        author = [],
        type = [],
        translations = [],
        journalsPublications = [],
        booksPublications = [],
        hypothesesPublications = [],
        access = [],
        years = {}
      },
      translations: _translations
    } = props;

    const tagsPlatforms = Object.keys(platforms)
      .filter(key => !!platforms[key])
      .map(i => ({
        label: getPlatformLabel(i),
        id: i,
        value: 0,
        facet: 'platform'
      }));

    const tagAccess = access.map(key => ({
      label: FacetFilter.getAccessLabel(key),
      id: key,
      value: 0,
      facet: 'access'
    }));

    const tagsAuthors = author.map(key => ({
      label: key,
      id: key,
      value: 0,
      facet: 'author'
    }));

    const tagsType = type.map(key => ({
      label: _translations[key] || key,
      id: key,
      value: 0,
      facet: 'type'
    }));

    const tagsTranslations = translations.map(key => ({
      label: langues[key] || key,
      id: key,
      value: 0,
      facet: 'translations'
    }));

    const _journalsPublications = platforms.OJ ? journalsPublications : [];
    const _booksPublications = platforms.OB ? booksPublications : [];
    const _hypothesesPublications = platforms.HO ? hypothesesPublications : [];

    const tagsJP = _journalsPublications.map(key => ({
      label: _translations[key] || key,
      id: key,
      value: 0,
      facet: 'journalsPublications'
    }));

    const tagsBP = _booksPublications.map(key => ({
      label: _translations[key] || key,
      id: key,
      value: 0,
      facet: 'booksPublications'
    }));

    const tagsHP = _hypothesesPublications.map(key => ({
      label: _translations[key] || key,
      id: key,
      value: 0,
      facet: 'hypothesesPublications'
    }));

    let tagsDate = '';
    if (years.mustUse) {
      tagsDate = [
        {
          label: `${years.min} - ${years.max}`,
          id: `${years.min} - ${years.max}`,
          value: 0,
          facet: 'years'
        }
      ];
    }

    return {
      ...state,
      tags: [
        ...tagsPlatforms,
        ...tagAccess,
        ...tagsAuthors,
        ...tagsType,
        ...tagsTranslations,
        ...tagsJP,
        ...tagsBP,
        ...tagsHP,
        ...tagsDate
      ]
    };
  }

  handleSelect = (option: SelectFacet): void => {
    const { tags } = this.state;
    const alreadySelected = tags.find(tag => tag.id === option.id);

    const newTags = alreadySelected
      ? tags.filter(tag => tag.id !== option.id)
      : [...tags, option];

    if (
      [
        'author',
        'type',
        'translations',
        'journalsPublications',
        'booksPublications',
        'hypothesesPublications',
        'access'
      ].includes(option.facet)
    ) {
      this.props.setCriteria({
        [option.facet]: newTags
          .filter(i => i.facet === option.facet)
          .map(i => i.id)
      });
    } else if (option.facet === 'platform') {
      this.props.setCriteria({
        platforms: {
          OJ: newTags.find(i => i.id === 'OJ') !== undefined,
          OB: newTags.find(i => i.id === 'OB') !== undefined,
          HO: newTags.find(i => i.id === 'HO') !== undefined,
          CO: newTags.find(i => i.id === 'CO') !== undefined
        },
        journalsPublications: initialState.journalsPublications,
        booksPublications: initialState.booksPublications,
        hypothesesPublications: initialState.hypothesesPublications
      });
    } else if (option.facet === 'years') {
      this.props.setYearsMustUse(false);
    }

    this.reRunSearch();
  };

  handleChangeYear = (years: Years) => {
    years.mustUse = true;
    this.props.setCriteria({ years });

    this.reRunSearch();
  };

  removeAllTag = (): void => {
    this.props.setCriteria({
      platforms: initialState.platforms,
      type: initialState.type,
      access: initialState.access,
      author: initialState.author,
      translations: initialState.translations,
      journalsPublications: initialState.journalsPublications,
      booksPublications: initialState.booksPublications,
      hypothesesPublications: initialState.hypothesesPublications,
      years: initialState.years
    });

    this.reRunSearch();
  };

  reRunSearch = () => {
    const {
      search: { pagination }
    } = this.props;

    this.props.setCriteria({ pagination: { ...pagination, currentPage: 1 } });
    this.props.makeSearch();
  };

  static getAccessLabel = label => {
    let id = '';

    if (label === 'info:eu-repo/semantics/openAccess')
      id = 'facetFilter.open_access';
    else if (label === 'info:eu-repo/semantics/restrictedAccess')
      id = 'facetFilter.restricted_access';
    else id = 'facetFilter.embargoed_access';

    return formatMessage({ id });
  };

  render() {
    const {
      counts: {
        platform: { counts: countedPlatform = {} } = {},
        translations: { counts: countedTranslations = {} } = {},
        type: { counts: countedType = {} } = {},
        author: { counts: countedAuthor = {} } = {},
        access: { counts: countedAccess = {} } = {},
        'journals-publication': { counts: countedJournals = {} } = {},
        'books-publication': { counts: countedBooks = {} } = {},
        'hypotheses-publication': { counts: countedHypos = {} } = {}
      },
      search: { years },
      translations
    } = this.props;
    const { tags } = this.state;

    const itemsPlatform = Object.keys(countedPlatform).map(key => ({
      label: getPlatformLabel(key),
      id: key,
      value: countedPlatform[key]
    }));

    const itemsAccess = Object.keys(countedAccess).map(key => {
      return {
        label: FacetFilter.getAccessLabel(key),
        id: key,
        value: countedAccess[key]
      };
    });

    const itemsLangues = Object.keys(countedTranslations).map(key => {
      const label = langues[key] || key;

      return {
        label,
        id: key,
        value: countedTranslations[key]
      };
    });

    const itemsType = Object.keys(countedType).map(key => {
      return {
        label: cleanLabel(translations[key] || key),
        id: key,
        value: countedType[key]
      };
    });

    const itemsAuthor = Object.keys(countedAuthor).map(key => {
      return {
        label: cleanLabel(key),
        id: key,
        value: countedAuthor[key]
      };
    });

    const itemsJournals = Object.keys(countedJournals).map(key => {
      return {
        label: cleanLabel(translations[key] || key),
        id: key,
        value: countedJournals[key]
      };
    });

    const itemsBooks = Object.keys(countedBooks).map(key => {
      return {
        label: cleanLabel(translations[key] || key),
        id: key,
        value: countedBooks[key]
      };
    });

    const itemsHypos = Object.keys(countedHypos).map(key => {
      return {
        label: cleanLabel(translations[key] || key),
        id: key,
        value: countedHypos[key]
      };
    });

    const platformsSelected = tags.filter(tag => tag.facet === 'platform');

    let typeName = formatMessage({ id: 'facetFilter.documents.type' });
    if (platformsSelected.length === 1) {
      if (platformsSelected[0].id === 'OB')
        typeName = formatMessage({ id: 'facetFilter.documents.type' });
      else if (platformsSelected[0].id === 'CO')
        typeName = formatMessage({ id: 'facetFilter.events.type' });
    }

    return (
      <Wrapper>
        {tags.length > 0 && (
          <div className="tags-container">
            {tags.map(tag => (
              <div key={tag.id} className="tag">
                <div
                  className="tag-container"
                  onClick={() => this.handleSelect(tag)}
                >
                  <div>{tag.label}</div>
                  <div className="icon-close">
                    <ReactSVG svgStyle={svgStyle} src={Close} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {tags.length > 0 && (
          <div className="remove-filters-container">
            <Button onClick={this.removeAllTag} secondary>
              <FormattedMessage id="facetFilter.delete_all_filters" />
            </Button>
          </div>
        )}

        {itemsPlatform.length > 1 && (
          <ComboFilter
            checkedItems={platformsSelected}
            facet="platform"
            onSelect={this.handleSelect}
            title={formatMessage({ id: 'facetFilter.platform' })}
            items={itemsPlatform}
          />
        )}
        {itemsAccess.length > 1 && (
          <ComboFilter
            checkedItems={tags.filter(tag => tag.facet === 'access')}
            facet="access"
            onSelect={this.handleSelect}
            title={formatMessage({ id: 'facetFilter.access.type' })}
            items={itemsAccess}
          />
        )}
        {itemsLangues.length > 1 && (
          <ComboFilter
            checkedItems={tags.filter(tag => tag.facet === 'translations')}
            facet="translations"
            onSelect={this.handleSelect}
            title={formatMessage({ id: 'facetFilter.languages' })}
            items={itemsLangues}
            canBeSort
          />
        )}
        {itemsType.length > 1 && (
          <ComboFilter
            checkedItems={tags.filter(tag => tag.facet === 'type')}
            facet="type"
            onSelect={this.handleSelect}
            title={typeName}
            items={itemsType}
            canBeSort
          />
        )}
        {itemsAuthor.length > 1 && (
          <ComboFilter
            checkedItems={tags.filter(tag => tag.facet === 'author')}
            facet="author"
            onSelect={this.handleSelect}
            title={formatMessage({
              id:
                platformsSelected.length === 0
                  ? 'facetFilter.authors_publication'
                  : 'facetFilter.authors'
            })}
            items={itemsAuthor}
            canBeSort
          />
        )}

        {itemsJournals.length > 1 && (
          <ComboFilter
            checkedItems={tags.filter(
              tag => tag.facet === 'journalsPublications'
            )}
            facet="journalsPublications"
            onSelect={this.handleSelect}
            title={formatMessage({ id: 'facetFilter.journals' })}
            items={itemsJournals}
            canBeSort
          />
        )}
        {itemsBooks.length > 1 && (
          <ComboFilter
            checkedItems={tags.filter(tag => tag.facet === 'booksPublications')}
            facet="booksPublications"
            onSelect={this.handleSelect}
            title={formatMessage({ id: 'facetFilter.publishers' })}
            items={itemsBooks}
            canBeSort
          />
        )}
        {itemsHypos.length > 1 && (
          <ComboFilter
            checkedItems={tags.filter(
              tag => tag.facet === 'hypothesesPublications'
            )}
            facet="hypothesesPublications"
            onSelect={this.handleSelect}
            title={formatMessage({ id: 'facetFilter.blogs' })}
            items={itemsHypos}
            canBeSort
          />
        )}

        <DateFilter
          title={formatMessage({ id: 'facetFilter.years' })}
          years={years}
          onChange={this.handleChangeYear}
        />
      </Wrapper>
    );
  }
}

const mapStateToProps = ({
  results,
  search,
  facets,
  translations: { translations }
}) => ({
  counts: facets.counts,
  search,
  translations
});

export default connect(
  mapStateToProps,
  dispatch => ({
    setCriteria: arg => dispatch(setCriteria(arg)),
    setYearsMustUse: arg => dispatch(setYearsMustUse(arg)),
    makeSearch: () => dispatch(makeSearch())
  })
)(FacetFilter);



// WEBPACK FOOTER //
// ./src/components/FacetFilter/index.js